import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { useCreateQuoteDraft } from '@/hooks/useQuotes';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export function NewQuote() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [search, setSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  const picker = usePatientPicker(activeClinicId, search);
  const create = useCreateQuoteDraft();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!activeClinicId) return;
    if (!selectedPatient) {
      toast.error(t('quotes.patient_required'));
      return;
    }
    try {
      const quote = await create.mutateAsync({
        clinic_id: activeClinicId,
        patient_id: selectedPatient.id,
        valid_until: validUntil || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success(t('quotes.toast_created'));
      navigate(`/quotes/${quote.id}`);
    } catch (err) {
      console.error('create quote draft failed', err);
      toast.error(t('quotes.save_error'));
    }
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="mx-auto max-w-3xl px-6">
          <button
            type="button"
            onClick={() => navigate('/economy/quotes')}
            className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('quotes.back_to_list')}
          </button>

          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
            {t('quotes.new_quote')}
          </h2>

          <form
            onSubmit={onSubmit}
            className="mt-6 space-y-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
          >
            {/* Patient picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('quotes.patient_picker_label')}
              </label>
              {selectedPatient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
                  <span className="text-gray-900 dark:text-white">
                    {selectedPatient.first_name} {selectedPatient.last_name}
                    {selectedPatient.pat_number && (
                      <span className="ml-2 font-mono text-xs text-gray-500">
                        {selectedPatient.pat_number}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedPatient(null)}
                    className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                  >
                    {t('common.edit')}
                  </button>
                </div>
              ) : (
                <div className="mt-1">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('quotes.patient_picker_placeholder')}
                    className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {picker.isLoading && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                    </div>
                  )}
                  {picker.data && picker.data.length > 0 && (
                    <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
                      {picker.data.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setSelectedPatient(p);
                            setSearch('');
                          }}
                          className="flex w-full items-center justify-between border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                        >
                          <span className="text-gray-900 dark:text-white">
                            {p.first_name} {p.last_name}
                          </span>
                          <span className="font-mono text-xs text-gray-500">
                            {p.pat_number ?? ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Input
              label={t('quotes.field_valid_until')}
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('quotes.field_notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/economy/quotes')}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending || !selectedPatient}>
                {create.isPending ? t('common.saving') : t('quotes.save_draft')}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
